import React, { useEffect, useMemo, useRef, useState } from 'react';

import { classNames } from '@/helpers/classNames';
import { InputAdornment, SvgIcon } from '@material-ui/core';

import { down } from '../../../../assets/icons';
import {
	AsaphDropdownMenu,
	AsaphDropdownMenuProps,
} from '../../AsaphDropdownMenu';
import { FormInput } from '../FormInput';
import styles from './Select.module.scss';

export type SelectProps = {
	className?: string;
	dropdownClassName?: string;
	listClassName?: string;
	items: AsaphDropdownMenuProps['items'];
	customContent?:
		| AsaphDropdownMenuProps['customContent']
		| ((close: () => void) => React.ReactNode);
	isOpen?: boolean;
	error?: boolean;
	disabled?: boolean;
	onClick?: () => void;
	onChange?: (
		value:
			| AsaphDropdownMenuProps['items'][0]['value']
			| AsaphDropdownMenuProps['items'][0]['label'],
	) => void;
	value?: string | number;
	withBorder?: boolean;
	placeholder?: string;
	fullDropdownWidth?: boolean;
};

export const Select: React.FC<SelectProps> = ({
	className,
	dropdownClassName,
	listClassName,
	isOpen: externalIsOpen,
	items,
	customContent,
	error,
	onClick,
	onChange,
	value,
	withBorder,
	placeholder,
	fullDropdownWidth = true,
	disabled = false,
}) => {
	const [isOpen, setIsOpen] = useState(externalIsOpen || false);
	const [fieldWidth, setFieldWidth] = useState(0);
	const ref = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (typeof externalIsOpen === 'boolean') {
			setIsOpen(externalIsOpen);
		}
	}, [externalIsOpen]);

	const renderedValue = useMemo(() => {
		return (
			items.find((i) => i.value === value)?.label ||
			value?.toString() ||
			''
		);
	}, [value, items]);

	return (
		<div
			className={classNames([
				styles['root'],
				disabled && styles['disabled'],
				className,
			])}
		>
			<FormInput
				placeholder={placeholder}
				id="service"
				name="service"
				value={renderedValue}
				disabled={disabled}
				endAdornment={
					<InputAdornment
						position="end"
						className={styles[isOpen ? 'open' : 'closed']}
					>
						<SvgIcon component={down} />
					</InputAdornment>
				}
				error={error}
				withBorder={withBorder}
				inputClassName={styles['input']}
			/>
			{!disabled && (
				<div
					ref={ref}
					className={styles['overlay']}
					onClick={() => {
						const width = ref.current?.clientWidth || 0;
						setFieldWidth(width > 20 ? width - 20 : width);
						setIsOpen(true);
						onClick && onClick();
					}}
				/>
			)}

			<AsaphDropdownMenu
				className={dropdownClassName}
				listClassName={listClassName}
				open={!!isOpen}
				anchorEl={ref.current}
				anchorOrigin={{
					horizontal: 'center',
				}}
				transformOrigin={{
					horizontal: 'center',
				}}
				onClose={() => {
					setIsOpen(false);
				}}
				items={items.map((i) => ({
					selected: i.label === value,
					...i,
					onClick: () => {
						if (i.onClick) {
							i.onClick();
						}
						setIsOpen(false);
						onChange &&
							onChange(
								i.value !== 'undefined' ? i.value : i.label,
							);
					},
				}))}
				customContent={
					typeof customContent === 'function'
						? customContent(() => setIsOpen(false))
						: customContent
				}
				width={fullDropdownWidth ? fieldWidth : undefined}
			/>
		</div>
	);
};
